import { platformApiFetch } from '@/services/platformApi/client';
import { getCurrentAuthUser } from '@/services/authSession';

import type {
  AdminGrantSubscriptionInput,
  CheckoutSessionResponse,
  SubscriptionStatus,
} from '@/hooks/subscription/types';

type EdgeResponse<T> = {
  success: boolean;
  data: T;
  error?: {
    code?: string;
    message?: string;
  };
};

type LegacySubscriptionSummary = {
  subscribed?: boolean;
  tier?: string | null;
  status?: string | null;
  subscription_source?: string | null;
  subscription_start?: string | null;
  subscription_end?: string | null;
  product_id?: string | null;
  price_id?: string | null;
};

const isTierCode = (value: unknown): value is SubscriptionStatus['tier'] => (
  value === 'free_tier' || value === 'whatsapp_tier' || value === 'premium_tier'
);

const isLifecycleStatus = (value: unknown): value is NonNullable<SubscriptionStatus['status']> => (
  value === 'free' ||
  value === 'active' ||
  value === 'grace_period' ||
  value === 'canceled' ||
  value === 'expired' ||
  value === 'scheduled_change' ||
  value === 'trialing' ||
  value === 'past_due' ||
  value === 'requires_action'
);

const getErrorMessage = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return '';
  }

  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' ? message : '';
};

const getErrorStatus = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const errorRecord = error as { status?: unknown; context?: { status?: unknown } };

  if (typeof errorRecord.status === 'number') {
    return errorRecord.status;
  }

  if (typeof errorRecord.context?.status === 'number') {
    return errorRecord.context.status;
  }

  return null;
};

const shouldFallbackToLegacyBilling = (error: unknown) => {
  const status = getErrorStatus(error);
  if (status === 404 || status === 500 || status === 502 || status === 503) {
    return true;
  }

  const message = getErrorMessage(error);
  return /requested function was not found|failed to fetch|networkerror|fetch|relay|does not exist|not found/i.test(message);
};

const normalizeLegacySubscriptionSummary = (payload: LegacySubscriptionSummary): SubscriptionStatus => {
  const tier = isTierCode(payload.tier) ? payload.tier : 'free_tier';
  const status = payload.status === 'pending_verification'
    ? 'requires_action'
    : isLifecycleStatus(payload.status)
      ? payload.status
      : payload.subscribed
        ? 'active'
        : 'free';
  const startsAt = payload.subscription_start ?? null;
  const expiresAt = payload.subscription_end ?? null;

  return {
    subscribed: Boolean(payload.subscribed),
    tier,
    status,
    subscription_source: payload.subscription_source === 'admin' || payload.subscription_source === 'stripe'
      ? payload.subscription_source
      : null,
    subscription_start: startsAt,
    subscription_end: expiresAt,
    startsAt,
    expiresAt,
    manageable: payload.subscription_source === 'stripe' && Boolean(payload.subscribed),
  };
};

const fetchLegacySubscriptionSummary = async (): Promise<SubscriptionStatus> => {
  const user = await getCurrentAuthUser();
  if (!user) {
    throw new Error('Authentication required');
  }

  const subscriberRow = await platformApiFetch<Record<string, unknown> | null>(`/v1/subscriptions/subscriber/${user.id}`);

  if (!subscriberRow) {
    return {
      subscribed: false,
      status: 'free',
      subscription_source: null,
      tier: 'free_tier',
      planName: 'Free',
      manageable: false,
    };
  }

  return normalizeLegacySubscriptionSummary({
    subscribed: Boolean(subscriberRow.subscribed),
    tier: typeof subscriberRow.tier_code === 'string'
      ? subscriberRow.tier_code
      : typeof subscriberRow.tier === 'string'
        ? subscriberRow.tier
        : null,
    status: typeof subscriberRow.status === 'string' ? subscriberRow.status : null,
    subscription_source: typeof subscriberRow.subscription_source === 'string' ? subscriberRow.subscription_source : null,
    subscription_start: typeof subscriberRow.starts_at === 'string'
      ? subscriberRow.starts_at
      : typeof subscriberRow.subscription_start === 'string'
        ? subscriberRow.subscription_start
        : null,
    subscription_end: typeof subscriberRow.expires_at === 'string'
      ? subscriberRow.expires_at
      : typeof subscriberRow.subscription_end === 'string'
        ? subscriberRow.subscription_end
        : null,
  });
};

const unwrapEdgeResponse = <T>(payload: EdgeResponse<T> | null, fallbackMessage: string) => {
  if (!payload?.success) {
    throw new Error(payload?.error?.message || fallbackMessage);
  }

  return payload.data;
};

type FetchSubscriptionSummaryOptions = {
  strictStripeSync?: boolean;
};

export async function fetchSubscriptionSummary(options: FetchSubscriptionSummaryOptions = {}): Promise<SubscriptionStatus> {
  try {
    if (options.strictStripeSync) {
      return platformApiFetch<SubscriptionStatus>('/v1/billing/subscription-summary');
    }
    return platformApiFetch<SubscriptionStatus>('/v1/billing/subscription-summary');
  } catch (error) {
    if (options.strictStripeSync) {
      throw error;
    }

    if (!shouldFallbackToLegacyBilling(error)) {
      throw error;
    }

    return fetchLegacySubscriptionSummary();
  }
}

export async function refreshSubscriptionSummary(): Promise<SubscriptionStatus> {
  await platformApiFetch('/v1/billing/refresh', {
    method: 'POST',
  });

  return fetchSubscriptionSummary();
}

export async function createCheckoutSession(input: {
  tierCode: 'whatsapp_tier' | 'premium_tier';
}): Promise<CheckoutSessionResponse> {
  return platformApiFetch<CheckoutSessionResponse>('/v1/billing/checkout-session', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function createCustomerPortalSession(): Promise<{ url: string }> {
  return platformApiFetch<{ url: string }>('/v1/billing/customer-portal-session', {
    method: 'POST',
  });
}

export async function grantAdminSubscription(input: AdminGrantSubscriptionInput) {
  return platformApiFetch<Record<string, unknown>>('/v1/admin/subscriptions/grant', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function extendAdminSubscription(input: {
  targetUserId: string;
  expiresAt: string;
  adminNotes?: string | null;
}) {
  return platformApiFetch<Record<string, unknown>>('/v1/admin/subscriptions/extend', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}